/**
 * User Context for centralized user data management
 * 
 * Provides:
 * - Centralized user data storage
 * - Automatic synchronization with server
 * - Cache management
 * - User data updates
 * 
 * @module contexts/UserContext
 */

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { apiClient } from "../utils/api/client";
import {
  getTelegramUserFromStorage,
  setTelegramUserInStorage,
  removeTelegramUserFromStorage,
} from "../utils/authStorage";
import type { TelegramUser, Progress } from "../types";

interface UserContextType {
  user: TelegramUser | null;
  loading: boolean;
  error: string | null;
  refreshUser: () => Promise<void>;
  updateUser: (updates: Partial<TelegramUser>) => void;
  clearUser: () => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

interface UserProviderProps {
  children: ReactNode;
  initialUser?: TelegramUser | null;
}

export function UserProvider({ children, initialUser }: UserProviderProps) {
  const [user, setUser] = useState<TelegramUser | null>(initialUser || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load user from localStorage on mount (key namespaced by hostname)
  useEffect(() => {
    const saved = getTelegramUserFromStorage();
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setUser(parsed);
      } catch {
        setUser(null);
      }
    }
  }, []);

  // Refresh user data from server
  const refreshUser = useCallback(async () => {
    // Используем функциональное обновление для получения актуального user.id
    let userId: number | undefined;
    setUser((prev) => {
      userId = prev?.id;
      return prev; // Не изменяем состояние здесь
    });
    
    if (!userId) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const data = await apiClient.get<TelegramUser>(`/user/${userId}`, {
        cacheTTL: 30 * 1000, // 30 seconds cache
      });

      // Merge with existing user data using functional update
      setUser((prev) => {
        if (!prev) return prev;
        const updated = {
          ...prev,
          ...data,
          progress: data.progress || prev.progress || {},
        };
        
        // Update localStorage
        setTelegramUserInStorage(JSON.stringify(updated));
        
        return updated;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to refresh user data");
      console.error("Error refreshing user:", err);
    } finally {
      setLoading(false);
    }
  }, []); // Убрали user?.id из зависимостей - используем функциональное обновление для получения user.id

  // Update user data locally
  const updateUser = useCallback((updates: Partial<TelegramUser>) => {
    setUser((prev) => {
      if (!prev) return prev;
      const updated = { ...prev, ...updates };
      setTelegramUserInStorage(JSON.stringify(updated));
      return updated;
    });
  }, []);

  // Clear user data
  const clearUser = useCallback(() => {
    setUser(null);
    removeTelegramUserFromStorage();
    // Invalidate user cache
    if (user?.id) {
      apiClient.invalidate(`/user/${user.id}`);
    }
  }, [user?.id]);

  // Auto-refresh user data periodically (every 5 minutes)
  useEffect(() => {
    if (!user?.id) return;

    const interval = setInterval(() => {
      refreshUser();
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(interval);
  }, [user?.id, refreshUser]);

  const value: UserContextType = {
    user,
    loading,
    error,
    refreshUser,
    updateUser,
    clearUser,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

/**
 * Hook to access user context
 */
export function useUser(): UserContextType {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
}

